import PropTypes from 'prop-types';
import React from 'react';
import { hook } from '@g4/automation-hooks';
import styled from 'styled-components';

export const propTypes = {
  /** Associates a specific action onto an element for the benefit of automated clients consuming it */
  automationHook: PropTypes.string,
  /** Sets the `type` attribute */
  htmlType: PropTypes.oneOf(['submit', 'reset', 'button']),
  /** Display type */
  type: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),
  /** Describes the background to assist the component in choosing an appropriate contrast */
  background: PropTypes.oneOf(['light', 'dark']),
  /** Displays full width */
  fullWidth: PropTypes.bool,
  /** Unique identifier to apply as an `id` attribute */
  id: PropTypes.string,
  /** Disabled status */
  disabled: PropTypes.bool,
  /**
   * A 'relationship attribute' which denotes which elements in a page the button has control
   * over
   */
  ariaControls: PropTypes.string,
  /**
   * `aria-describedby` is used to indicate the IDs of the elements that describe the object.
   * It is used to establish a relationship between widgets or groups and text that describes
   * them
   */
  ariaDescribedby: PropTypes.string,
  /** Button click handler */
  onClick: PropTypes.func,
};

export const defaultProps = {
  /**
   * The default type for a standard <button> is 'submit':
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#attr-type
   */
  htmlType: 'button',
  type: 'primary',
  background: 'light',
  fullWidth: false,
  onClick: Function.prototype,
};

export const ButtonText = styled.span``;

export const Button = styled(props => (
  <button
    className={props.className}
    id={props.id}
    type={props.htmlType}
    onClick={props.onClick}
    disabled={props.disabled}
    {...hook(props.automationHook)}
    children={props.children}
    aria-controls={props.ariaControls}
    aria-describedby={props.ariaDescribedby}
  >
    <ButtonText>{props.children}</ButtonText>
  </button>
))`
  display: inline-block;
  position: relative;
  max-width: 300px;
  min-height: 44px;
  padding: ${props => props.theme.spacing(1)} ${props =>
  props.theme.spacing(3)};
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.875rem;
  text-transform: uppercase;
  cursor: pointer;

  ${props =>
    props.fullWidth &&
    `
      width: 100%;
      max-width: none;
    `}

  ${({ theme: { colors }, ...props }) => {
    const styles = {};

    const disabledStyles = `
      background: ${colors.button.disabled};
      color: ${colors.button.lightText};
      cursor: default;
    `;

    const secondaryButtonStyles = `
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      width: calc(100% - 4px);
      height: calc(100% - 4px);
      ${props.fullWidth ? 'max-width: none;' : ''}
      top: 2px;
      left: 2px;
      border-radius: 6px;
      content: '';
    `;

    styles.primary = {
      light: {
        disabled: `
          ${disabledStyles}
        `,
        enabled: `
          background: ${colors.button.primary};
          color: ${colors.button.darkText};

          span {
            position: relative;
            z-index: 1;
          }

          &:hover,
          &:active {
            &::after {
              display: flex;
              justify-content: center;
              align-items: center;
              position: absolute;
              width: 100%;
              min-height: 44px;
              top: 0;
              left: 0;
              border-radius: 8px;
              background: ${colors.button.hoverPrimary};
              content: '';
            }
          }
        `,
      },
      dark: {
        disabled: `
          ${disabledStyles}
        `,
        enabled: `
          background: ${colors.button.primary};

          &:hover,
          &:active {
            background: ${colors.button.hoverPrimary};
          }
        `,
      },
    };

    styles.secondary = {
      light: {
        disabled: `
          ${disabledStyles}
        `,
        enabled: `
          position: relative;
          background: ${colors.button.primary};
          color: ${colors.button.darkText};

          > ${ButtonText} {
            position: relative;
            z-index: 1;
          }

          &::before {
            ${secondaryButtonStyles}
            background-color: ${colors.button.lightText};
          }

          &:hover,
          &:active {
            &::after {
              ${secondaryButtonStyles}
              min-height: 40px;
              background: ${colors.button.primary};
            }
          }
        `,
      },
      dark: {
        disabled: `
          ${disabledStyles}
        `,
        enabled: `
          position: relative;
          background: ${colors.button.primary};
          color: ${colors.button.lightText};

          > ${ButtonText} {
            position: relative;
            z-index: 1;
          }

          &::before {
            ${secondaryButtonStyles}
            background: ${colors.button.secondaryDark};
          }

          &:hover,
          &:active {
            > ${ButtonText} {
              color: ${colors.button.darkText};
            }

            &::after {
              ${secondaryButtonStyles}
              background: ${colors.button.hoverSecondary};
            }
          }
        `,
      },
    };

    styles.tertiary = {
      light: {
        enabled: `
          color: ${colors.button.darkText};
        `,
        disabled: `
          color: ${colors.button.disabled};
          cursor: default;
        `,
      },
      dark: {
        disabled: `
          color: ${colors.button.tertiaryDisabled};
        `,
        enabled: `
          color: ${colors.button.lightText};
        `,
      },
    };

    return styles[props.type][props.background][
      props.disabled ? 'disabled' : 'enabled'
    ];
  }}

  ${props =>
    props.as === 'a' &&
    `
    display: inline;
    background: none;

    &:hover,
    &:active {
      &::after {
        background: none;
      }
    }
  `}
`;

Button.propTypes = propTypes;
Button.defaultProps = defaultProps;

export default Button;
